import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantLlmConfigUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { LlmProviderCreate } from "../../../llmProvider/components/create/LlmProviderCreate";
import { LlmProviderCreateStore } from "../../../llmProvider/store";
import { InputString } from "../../../common/controls";
import { InputSecret } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantLlmConfigUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  providerSelectedValue?: { displayId: string; id: string } | null;
  onProviderChange?: (value: string) => void;
  onProviderSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  providerOptions?: { displayId: string; id: string }[];
  providerError?: string;
  model: string;
  modelSelectedValue?: { displayId: string; id: string } | null;
  onModelChange?: (value: string) => void;
  modelError?: string;
  apiKey: string;
  apiKeySelectedValue?: { displayId: string; id: string } | null;
  onApiKeyChange?: (value: string) => void;
  apiKeyError?: string;
  baseUrl: string;
  baseUrlSelectedValue?: { displayId: string; id: string } | null;
  onBaseUrlChange?: (value: string) => void;
  baseUrlError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  priority: number;
  prioritySelectedValue?: { displayId: string; id: string } | null;
  onPriorityChange?: (value: number) => void;
  priorityError?: string;
  maxTokens: number | undefined;
  maxTokensSelectedValue?: { displayId: string; id: string } | null;
  onMaxTokensChange?: (value: number | undefined) => void;
  maxTokensError?: string;
  temperature: number | undefined;
  temperatureSelectedValue?: { displayId: string; id: string } | null;
  onTemperatureChange?: (value: number | undefined) => void;
  temperatureError?: string;
  debugFullMode: boolean;
  debugFullModeSelectedValue?: { displayId: string; id: string } | null;
  onDebugFullModeChange?: (value: boolean) => void;
  debugFullModeError?: string;
  tenantLlmConfigUpdateStore: TenantLlmConfigUpdateStore;
};

export const TenantLlmConfigUpdateForm: React.FC<TenantLlmConfigUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onProviderSelected = () => {},
      providerOptions = [],
      providerSelectedValue = null,
      providerError,
      onProviderChange = () => {},
      model = "",
      modelError,
      onModelChange = () => {},
      apiKey = "",
      apiKeyError,
      onApiKeyChange = () => {},
      baseUrl = "",
      baseUrlError,
      onBaseUrlChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      priority = 0,
      priorityError,
      onPriorityChange = () => {},
      maxTokens = undefined,
      maxTokensError,
      onMaxTokensChange = () => {},
      temperature = undefined,
      temperatureError,
      onTemperatureChange = () => {},
      debugFullMode = false,
      debugFullModeError,
      onDebugFullModeChange = () => {},
      tenantLlmConfigUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantLlmConfig"
              mode="update"
              values={{}}
              options={{ provider: providerOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "provider":
                    onProviderSelected?.(valor);
                    break;
                  case "model":
                    onModelChange?.(valor);
                    break;
                  case "apiKey":
                    onApiKeyChange?.(valor);
                    break;
                  case "baseUrl":
                    onBaseUrlChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "priority":
                    onPriorityChange?.(valor);
                    break;
                  case "maxTokens":
                    onMaxTokensChange?.(valor);
                    break;
                  case "temperature":
                    onTemperatureChange?.(valor);
                    break;
                  case "debugFullMode":
                    onDebugFullModeChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantLlmConfigUpdateStore.getCardErrors("null")}
              title={langStore.groupTenantLlmConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="provider"
                  label={langStore.TenantLlmConfigProviderLabel}
                  autoFocus
                  error={Boolean(providerError)}
                  helperText={
                    providerError ||
                    tenantLlmConfigUpdateStore.warnings["provider"]?.message
                  }
                  selectedValue={providerSelectedValue}
                  onChange={(value: string) => onProviderChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onProviderSelected(value)
                  }
                  options={providerOptions}
                  description={langStore.TenantLlmConfigProviderDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "llmProvider_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new LlmProviderCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <LlmProviderCreate
                          llmProviderCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewLlmProvider"
                      cancelMethod="cancelNewLlmProvider"
                    />
                  )}
                />
                <InputString
                  value={model}
                  error={Boolean(modelError)}
                  warning={
                    !modelError &&
                    Boolean(tenantLlmConfigUpdateStore.warnings["model"])
                  }
                  helperText={
                    modelError ||
                    tenantLlmConfigUpdateStore.warnings["model"]?.message
                  }
                  label={langStore.TenantLlmConfigModelLabel}
                  onChange={(value: string) => onModelChange(value)}
                  description={langStore.TenantLlmConfigModelDescription}
                />
                <InputSecret
                  value={apiKey}
                  error={Boolean(apiKeyError)}
                  helperText={
                    apiKeyError ||
                    tenantLlmConfigUpdateStore.warnings["apiKey"]?.message
                  }
                  label={langStore.TenantLlmConfigApiKeyLabel}
                  onChange={(value: string) => onApiKeyChange(value)}
                  description={langStore.TenantLlmConfigApiKeyDescription}
                />
                <InputString
                  value={baseUrl}
                  error={Boolean(baseUrlError)}
                  warning={
                    !baseUrlError &&
                    Boolean(tenantLlmConfigUpdateStore.warnings["baseUrl"])
                  }
                  helperText={
                    baseUrlError ||
                    tenantLlmConfigUpdateStore.warnings["baseUrl"]?.message
                  }
                  label={langStore.TenantLlmConfigBaseUrlLabel}
                  onChange={(value: string) => onBaseUrlChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigBaseUrlDescription}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    tenantLlmConfigUpdateStore.warnings["enabled"]?.message
                  }
                  label={langStore.TenantLlmConfigEnabledLabel}
                  description={langStore.TenantLlmConfigEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputNumber
                  value={priority}
                  error={Boolean(priorityError)}
                  warning={
                    !priorityError &&
                    Boolean(tenantLlmConfigUpdateStore.warnings["priority"])
                  }
                  helperText={
                    priorityError ||
                    tenantLlmConfigUpdateStore.warnings["priority"]?.message
                  }
                  label={langStore.TenantLlmConfigPriorityLabel}
                  decimalScale={0}
                  description={langStore.TenantLlmConfigPriorityDescription}
                  onChange={(value: number) => onPriorityChange(value)}
                />
                <InputNumber
                  value={maxTokens}
                  nullable={true}
                  error={Boolean(maxTokensError)}
                  warning={
                    !maxTokensError &&
                    Boolean(tenantLlmConfigUpdateStore.warnings["maxTokens"])
                  }
                  helperText={
                    maxTokensError ||
                    tenantLlmConfigUpdateStore.warnings["maxTokens"]?.message
                  }
                  label={langStore.TenantLlmConfigMaxTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigMaxTokensDescription}
                  onChange={(value: number | undefined) =>
                    onMaxTokensChange(value)
                  }
                />
                <InputNumber
                  value={temperature}
                  nullable={true}
                  error={Boolean(temperatureError)}
                  warning={
                    !temperatureError &&
                    Boolean(tenantLlmConfigUpdateStore.warnings["temperature"])
                  }
                  helperText={
                    temperatureError ||
                    tenantLlmConfigUpdateStore.warnings["temperature"]?.message
                  }
                  label={langStore.TenantLlmConfigTemperatureLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigTemperatureDescription}
                  onChange={(value: number | undefined) =>
                    onTemperatureChange(value)
                  }
                />
                <InputBoolean
                  value={debugFullMode}
                  error={Boolean(debugFullModeError)}
                  helperText={
                    debugFullModeError ||
                    tenantLlmConfigUpdateStore.warnings["debugFullMode"]
                      ?.message
                  }
                  label={langStore.TenantLlmConfigDebugFullModeLabel}
                  description={
                    langStore.TenantLlmConfigDebugFullModeDescription
                  }
                  onChange={(value: boolean) => onDebugFullModeChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
